/**
 * Authenticated API helper
 * Handles token retrieval and API calls with Bearer authentication
 * Supports both same-origin (combined) and cross-origin (separated) deployment
 * Uses Azure AD authentication via MSAL
 */

import { msalInstance } from '../components/Auth/authConfig';
import { buildApiUrl, isCrossOriginApi } from '../config/apiConfig';

/**
 * Get access token for API calls (Azure AD via MSAL)
 */
export async function getApiToken(): Promise<string | null> {
  try {
    // Check if MSAL is initialized
    const config = msalInstance.getConfiguration();
    if (!config || !config.auth) {
      console.warn('[AUTH_API] MSAL not initialized yet');
      return null;
    }

    // Wait a moment for any ongoing initialization
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    const request = {
      scopes: ['User.Read', 'Mail.Read', 'Files.Read.All', 'Calendars.Read', 'Chat.Read', 'ChannelMessage.Read.All', 'Team.ReadBasic.All', 'Channel.ReadBasic.All'],
      account: account,
    };

    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    console.error('[AUTH_API] Failed to get API token:', error);
    return null;
  }
}

/**
 * Make authenticated API call
 */
export async function authenticatedFetch(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  const token = await getApiToken();
  
  const headers: Record<string, string> = {
    ...options.headers as Record<string, string>,
  };

  // Only set Content-Type for non-FormData requests
  // FormData requests need the browser to set multipart/form-data with boundary
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Add Authorization header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Build full API URL (handles both same-origin and cross-origin)
  const fullUrl = buildApiUrl(url);
  
  // For cross-origin requests, we need credentials to be included
  const credentials = isCrossOriginApi() ? 'include' : 'same-origin';

  return fetch(fullUrl, {
    ...options,
    headers,
    credentials,
  });
}

/**
 * Check if user has valid authentication (Azure AD)
 */
export function isAuthenticated(): boolean {
  const account = msalInstance.getActiveAccount();
  return !!account;
}

/**
 * Get current user's UPN (User Principal Name) from Azure AD
 */
export function getCurrentUserUPN(): string | null {
  const account = msalInstance.getActiveAccount();
  return account?.username || null;
}

/**
 * Simple API fetch without authentication
 * Still handles cross-origin API URL building
 */
export function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const fullUrl = buildApiUrl(url);
  const credentials = isCrossOriginApi() ? 'include' : 'same-origin';
  
  return fetch(fullUrl, {
    ...options,
    credentials,
  });
}
